import React, { FC, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import InitialsAvatar from 'src/components/elements/initials-avatar/InitialsAvatar'
import { HeadSection, VerificationBadge } from 'src/components/elements'
import { Button, InputField, MyModal, TextAreaField } from 'src/components/ui'
import { formatPrice } from 'src/helpers/formatPrice'
import { getRelativeTime } from 'src/helpers/dateHelpers'
import { AuctionIcon, ChevronNextIcon, PhoneIcon } from '@svgs'
import { IProperty } from 'src/interfaces/IProperty'
import { Rating, Slider, styled } from '@mui/material'
import {
  useCreateBidMutation,
  useGetAuctionInfoQuery,
  useGetUserQuery,
  useSendChatMessageMutation,
} from 'src/redux/api'
import { toast } from 'react-toastify'
import GuestInquiryModal from 'src/components/elements/modals/guest-inquiry/GuestInquiryModal'
import { socket } from 'src/socket'
import { ChatEvent } from 'src/redux/socketApi'
import ContactRevealButton from './ContactRevealButton'
import ContactRevealsList from './ContactRevealsList'
import OfferBox from './OfferBox'
import ReviewsBlock from './ReviewsBlock'
import { errorText } from 'src/helpers/errorText'

interface IAgentInfo {
  property?: IProperty
}

const AgentInfo: FC<IAgentInfo> = ({ property }) => {
  const { t } = useTranslation()
  const { data: currentUser } = useGetUserQuery(undefined)
  if (!property) return null
  const isGuest = !currentUser
  const [openModal, setOpenModal] = useState(false)
  const [openInquiry, setOpenInquiry] = useState(false)
  const [value, setValue] = useState(
    (property.auctionSection?.currentPrice || 0) + (property.auctionSection?.minStep || 0),
  )
  const [messageText, setMessageText] = useState('')
  const [createBid, { isLoading, error }] = useCreateBidMutation()
  const [sendChatMessage, { isLoading: isSendingMessage }] = useSendChatMessageMutation()

  // Используем данные о пользователе из объявления
  const user = property.user
  const isAuction = property.auctionSection?.isAuction
  // Contacts-on-request (B1): the owner sees the "who opened" log instead of a
  // reveal button; everyone else asks before the phone appears.
  const isOwner = !!currentUser && currentUser.id === user.id

  // Bid history + live updates. Joining the auction room and refetching on a
  // socket bid keeps the price/history current for everyone viewing it; the
  // emit is a no-op where the socket is not connected (e.g. the public page).
  const { data: auctionInfo, refetch: refetchAuction } = useGetAuctionInfoQuery(property.id, {
    skip: !isAuction,
  })

  useEffect(() => {
    if (!isAuction) return
    socket.emit(ChatEvent.JoinAuction, { propertyId: property.id })
    const onBid = (data: { propertyId: string }) => {
      if (data.propertyId === property.id) refetchAuction()
    }
    const onOutbid = (data: { propertyId: string }) => {
      if (data.propertyId === property.id) toast.info(t('agent.outbid', 'You have been outbid'))
    }
    socket.on(ChatEvent.AuctionBid, onBid)
    socket.on(ChatEvent.AuctionOutbid, onOutbid)
    return () => {
      socket.emit(ChatEvent.LeaveAuction, { propertyId: property.id })
      socket.off(ChatEvent.AuctionBid, onBid)
      socket.off(ChatEvent.AuctionOutbid, onOutbid)
    }
  }, [isAuction, property.id, refetchAuction, t])

  // Clamp at submit time too: the input's onBlur clamp may not have landed in
  // state yet when the tap goes straight from the field to the button (39.9)
  const handleBid = useCallback(async () => {
    const min =
      (property.auctionSection?.currentPrice || 0) + (property.auctionSection?.minStep || 0)
    const max =
      (property.auctionSection?.currentPrice || 0) + (property.auctionSection?.maxStep || 0)
    const amount = Math.min(Math.max(value, min), max)
    setValue(amount)
    try {
      await createBid({ propertyId: property.id, amount }).unwrap()
      setOpenModal(false)
    } catch {
      // the error banner in the modal shows the backend message
    }
  }, [property.id, property.auctionSection, value, createBid])

  const handleSendMessage = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()

      if (!messageText.trim()) {
        toast.warning(t('agent.messageRequired', 'Please enter a message'))
        return
      }

      try {
        await sendChatMessage({
          text: messageText,
          recipientId: user.id, // Send to property owner
        }).unwrap()

        toast.success(t('agent.messageSent', 'Message sent successfully!'))
        setMessageText('') // Clear the form
      } catch {
        // Error toast is automatically handled by toastMiddleware
      }
    },
    [messageText, user.id, sendChatMessage, t],
  )

  const auctionMinPrice =
    (property.auctionSection?.currentPrice || 0) + (property.auctionSection?.minStep || 0)
  const auctionMaxPrice =
    (property.auctionSection?.currentPrice || 0) + (property.auctionSection?.maxStep || 0)

  useEffect(() => {
    setValue((property.auctionSection?.currentPrice || 0) + (property.auctionSection?.minStep || 0))
  }, [property.auctionSection?.currentPrice, property.auctionSection?.minStep])

  return (
    // The seller card (design system: white, radius 16, the card shadow)
    <div
      data-seller-card
      className="flex flex-col gap-4 p-5 md:p-6 w-full bg-surface rounded-2xl shadow-card"
    >
      <div className="flex items-start gap-3.5 w-full">
        <div className="w-14 h-14 shrink-0 relative">
          <div className="size-full rounded-full overflow-hidden">
            <InitialsAvatar
              src={user.avatar}
              name={`${user.firstName || ''} ${user.lastName || ''}`}
            />
          </div>
          {/* Real presence (I2/B5): green when online, grey when offline with
              a known last-seen; hidden when the owner turned status off. */}
          {(user.isOnline || user.lastSeen) && (
            <div className="w-4 h-4 bg-surface rounded-full justify-center items-center inline-flex pointer-events-none absolute bottom-0 right-0">
              <div
                className={`w-2.5 h-2.5 rounded-full ${
                  user.isOnline ? 'bg-success' : 'bg-control'
                }`}
              />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1 text-sm text-ink min-w-0 break-words">
          <div className="text-lg font-extrabold leading-6">
            {user.firstName} {user.lastName}
          </div>
          <div className="font-bold text-ink-2">{t('agent.owner')}</div>
          {/* Diia is black and white: its white mark stays on a dark pill */}
          <VerificationBadge
            className="!bg-inverse !px-2.5 !py-1 !rounded-full !w-auto !self-start gap-2"
            verified={user.diiaVerified}
          />
          {/* Presence line (I2/B5): matches the messenger — online now, or the
              relative last-seen; hidden when the owner disabled their status. */}
          {user.isOnline ? (
            <div className="flex items-center gap-1.5 text-xs font-bold text-success">
              <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-success" />
              {t('online')}
            </div>
          ) : user.lastSeen ? (
            <div className="text-xs text-ink-2">
              {t('lastSeen', { time: getRelativeTime(user.lastSeen) })}
            </div>
          ) : null}
          {/* Rating summary at a glance (B4 redesign): stars + score/count. */}
          <div className="flex flex-wrap items-center gap-x-1.5">
            <Rating value={user.avgRating || 0} precision={0.5} readOnly size="small" />
            <span className="text-xs text-ink-2">
              {user.reviewsCount
                ? `${(user.avgRating || 0).toFixed(1)} (${user.reviewsCount})`
                : t('review.noReviews')}
            </span>
          </div>
        </div>
      </div>
      {/* How long they are here and how much they list, in one line — a new
          account used to read "0 років" in an 11px dark box */}
      <p className="m-0 text-sm leading-5 text-ink-2">
        {[
          user.yearsOnSite
            ? t('agent.member_years', { count: user.yearsOnSite })
            : t('agent.member_less_than_year'),
          t('agent.listings_count', { count: user.totalProperties || 0 }),
          t('agent.active_count', { count: user.activeProperties || 0 }),
        ].join(' · ')}
      </p>
      <div className="flex flex-col gap-3 w-full pt-4 border-0 border-t border-solid border-ink/[0.08]">
        <h3 className="m-0 text-lg font-extrabold leading-6 text-ink">
          {t('agent.contactSeller')}
        </h3>
        {isGuest ? (
          // Logged-out visitor: chat requires an account, so offer a guest
          // inquiry that lands in the admin inbox.
          <div className="flex flex-col gap-2 w-full text-sm">
            <Button
              type="button"
              onClick={() => setOpenInquiry(true)}
              className="w-full"
              iconAfter={<ChevronNextIcon />}
            >
              {t('guest_inquiry.cta')}
            </Button>
            {user.phone && (
              <Button
                link={`tel:${user.phone}`}
                variant="outlined"
                className="w-full"
                icon={<PhoneIcon />}
              >
                {user.phone}
              </Button>
            )}
            <GuestInquiryModal
              open={openInquiry}
              handleClose={() => setOpenInquiry(false)}
              propertyId={property.id}
            />
          </div>
        ) : (
          <form onSubmit={handleSendMessage} className="flex flex-col gap-2 w-full text-sm">
            <TextAreaField
              name="messageText"
              placeholder={t('agent.messagePlaceholder')}
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
            />
            <div className="flex flex-col gap-2">
              <Button
                type="submit"
                disabled={isSendingMessage || !messageText.trim()}
                className="w-full"
                iconAfter={<ChevronNextIcon />}
              >
                {isSendingMessage ? t('agent.sending', 'Sending...') : t('agent.sendMessage')}
              </Button>
              {!isOwner && <ContactRevealButton property={property} />}
              {user.phone && (
                <Button
                  link={`tel:${user.phone}`}
                  variant="outlined"
                  className="w-full"
                  icon={<PhoneIcon />}
                >
                  {user.phone}
                </Button>
              )}
              {isAuction &&
                property.auctionSection?.endDate &&
                new Date(property.auctionSection.endDate) > new Date() && (
                  <div className="flex-col gap-2 flex w-full pt-2">
                    <div className="text-base font-extrabold leading-6 text-ink">
                      {t('agent.offerYourPrice')}
                    </div>
                    <div className="flex items-center justify-between gap-2 min-h-11 pl-2 pr-1 py-1 bg-surface rounded-xl border border-solid border-ink/[0.18]">
                      <div className="flex items-center justify-center w-7 h-7 shrink-0 rounded-lg bg-success-soft">
                        <div className="w-4 h-4 icon-button fill-success">
                          <AuctionIcon />
                        </div>
                      </div>
                      <div className="grow shrink basis-0 flex justify-center items-baseline gap-1">
                        {/* Hardcoded «грн» showed for USD/EUR auctions too (39.9) */}
                        <div className="text-lg font-black leading-6 text-ink">
                          {formatPrice(
                            property.auctionSection?.currentPrice || 0,
                            property.currency,
                          )}
                        </div>
                      </div>
                      <Button type="button" size="sm" onClick={() => setOpenModal(true)}>
                        {t('agent.offer')}
                      </Button>
                      {/* min-w-[30rem] alone pushed the modal past a 390px
                          screen — full-width on phones (39.9) */}
                      <MyModal
                        width="w-[calc(100%_-_2rem)] md:w-auto md:min-w-[30rem]"
                        open={openModal}
                        handleClose={() => setOpenModal(false)}
                      >
                        <div className="p-5">
                          <HeadSection title={t('agent.offerYourPrice')} />
                          <span className="text-danger text-sm">
                            {error ? errorText(error) : ''}
                          </span>
                          <div className="flex flex-col gap-4">
                            <div className="flex flex-col gap-1">
                              {/* Numeric input + slider (39.9): a slider alone
                                  is too coarse for a price on a phone */}
                              <InputField
                                name="bidAmount"
                                type="number"
                                label={t('agent.yourPrice')}
                                value={String(value)}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                  setValue(Number(e.target.value))
                                }
                                onBlur={() =>
                                  setValue(
                                    Math.min(Math.max(value, auctionMinPrice), auctionMaxPrice),
                                  )
                                }
                              />
                              <IOSSlider
                                marks={[
                                  {
                                    value: auctionMinPrice,
                                    label: formatPrice(auctionMinPrice, property.currency),
                                  },
                                  {
                                    value: auctionMaxPrice,
                                    label: formatPrice(auctionMaxPrice, property.currency),
                                  },
                                ]}
                                track="normal"
                                step={100}
                                valueLabelDisplay="auto"
                                value={Math.min(Math.max(value, auctionMinPrice), auctionMaxPrice)}
                                onChange={(e, newValue) => setValue(newValue as number)}
                                min={auctionMinPrice}
                                max={auctionMaxPrice}
                              />
                            </div>
                            <Button
                              isLoading={isLoading}
                              type="button"
                              className="!h-11"
                              onClick={handleBid}
                            >
                              {t('agent.offer')}
                            </Button>
                          </div>
                        </div>
                      </MyModal>
                    </div>
                  </div>
                )}
            </div>
          </form>
        )}
      </div>
      {/* Ratings & reviews for this owner (H1/B4). Everyone sees the score;
          only a logged-in non-owner viewer may leave a review. */}
      <ReviewsBlock
        propertyId={property.id}
        revieweeId={user.id}
        canLeave={!isGuest && !isOwner && !!user.canReview}
        needsInteraction={!isGuest && !isOwner && !user.canReview}
        isReviewee={isOwner}
      />
      {/* Structured bargaining (B2): the buyer negotiates with the owner here;
          the owner negotiates per-buyer inside the "who opened" list. */}
      {!isGuest && !isOwner && (
        <OfferBox
          propertyId={property.id}
          counterpartId={user.id}
          defaultCurrency={property.currency}
        />
      )}
      {isOwner && (
        <ContactRevealsList propertyId={property.id} defaultCurrency={property.currency} />
      )}
      {isAuction && auctionInfo && auctionInfo.bids.length > 0 && (
        <div className="flex flex-col gap-2 w-full pt-4 border-0 border-t border-solid border-ink/[0.08]">
          <h4 className="m-0 text-sm font-extrabold leading-5 text-ink">
            {t('agent.bid_history', 'Bid history')}
          </h4>
          <ul className="flex flex-col gap-1 m-0 p-0 list-none">
            {auctionInfo.bids.slice(0, 8).map((bid) => (
              <li
                key={bid.id}
                className="flex justify-between items-center gap-2 text-sm bg-page rounded-lg px-3 py-2"
              >
                <span className="text-ink min-w-0 truncate">
                  {bid.user.firstName} {bid.user.lastName}
                </span>
                <span className="font-black text-ink shrink-0">
                  {formatPrice(bid.amount, property.currency)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default AgentInfo

const iOSBoxShadow =
  '0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.13),0 0 0 1px rgba(0,0,0,0.02)'
const IOSSlider = styled(Slider)(({ theme }) => ({
  color: 'rgb(var(--brand))',
  height: 5,
  padding: '15px 0',
  '& .MuiSlider-thumb': {
    height: 20,
    width: 20,
    backgroundColor: 'rgb(var(--brand))',
    boxShadow: '0 0 2px 0px rgba(0, 0, 0, 0.1)',
    '&:focus, &:hover, &.Mui-active': {
      boxShadow: '0px 0px 3px 1px rgba(0, 0, 0, 0.1)',
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        boxShadow: iOSBoxShadow,
      },
    },
    '&:before': {
      boxShadow:
        '0px 0px 1px 0px rgba(0,0,0,0.2), 0px 0px 0px 0px rgba(0,0,0,0.14), 0px 0px 1px 0px rgba(0,0,0,0.12)',
    },
  },
  '& .MuiSlider-valueLabel': {
    fontSize: 12,
    fontWeight: 'normal',
    fontFamily: 'Inter',
    top: -6,
    backgroundColor: 'rgb(var(--surface))',
    color: theme.palette.text.primary,
    '&::before': {
      display: 'none',
    },
    '& *': {
      background: 'transparent',
      color: 'rgb(var(--ink))',
    },
  },
  '& .MuiSlider-track': {
    border: 'none',
    height: 5,
  },
  '& .MuiSlider-rail': {
    opacity: 0.5,
    boxShadow: 'inset 0px 0px 4px -2px #000',
    backgroundColor: 'rgb(var(--ink) / 0.2)',
  },
}))
